import {
  ComponentFactoryResolver,
  Inject,
  Injectable
} from '@angular/core';
import { Subject } from 'rxjs';
import {
  ChartFilterPatternNameEnum,
  ChartType,
  ChartTypeEnum,
  CustomControlsNameEnum,
  IChartFilter,
  IChartFilterPattern,
  ILayoutEntity,
  ILayoutSettings,
  LayoutNameEnum,
  LayoutType,
  OptionType,
  OptionTypeEnum
} from 'app/charts-dashboard/interfaces';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { ChartsDashboardQuery } from '../state/dashboard';
import { ID } from '@datorama/akita';

@Injectable()
export class ChartsCommonService {

  constructor(
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly chartsDashboardQuery: ChartsDashboardQuery,
    @Inject(AppStore) private store: Redux.Store<AppState>
  ) {
  }

  public $takeScreenshot = new Subject<ID>();

  public isHistorical(chartType: ChartType): boolean {
    return chartType === ChartTypeEnum.SPOT_CHART ||
      chartType === ChartTypeEnum.IVX_HISTORY ||
      chartType === ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME ||
      chartType === ChartTypeEnum.HISTORICAL_VOLATILITY ||
      chartType === ChartTypeEnum.IMPLIED_VOLATILITY ||
      chartType === ChartTypeEnum.PREMIUM_CHART ||
      chartType === ChartTypeEnum.IV_DIFFERENCE ||
      chartType === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER;
  }

  public isMaturity(chartType: ChartType): boolean {
    return chartType === ChartTypeEnum.TERM_STRUCTURE_ROLLING ||
      chartType === ChartTypeEnum.SKEW_CHART;
  }

  public getChartName(chartType: ChartType): string {
    switch (chartType) {
      case ChartTypeEnum.HISTORICAL_VOLATILITY:
      case ChartTypeEnum.IMPLIED_VOLATILITY:
      case ChartTypeEnum.SKEW_CHART:
      case ChartTypeEnum.SINGLE_CONTRACT_ANALYZER:
        return chartType.split('_').map((s: string) => s.charAt(0) + s.slice(1).toLocaleLowerCase()).join(' ');
      case ChartTypeEnum.SPOT_CHART:
        return 'Spot Price';
      case ChartTypeEnum.IVX_HISTORY:
        return 'IV Index';
      case ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME:
        return 'Total OI and Volume';
      case ChartTypeEnum.TERM_STRUCTURE_ROLLING:
        return 'Term Structure';
      case ChartTypeEnum.PREMIUM_CHART:
        return 'IV Premium Chart';
      case ChartTypeEnum.IV_DIFFERENCE:
        return 'IV Difference';
    }
  }

  public convertLayoutToGridSettings(menuValue: ILayoutEntity): ILayoutSettings {
    const layoutSettings: ILayoutSettings = {
      columnsSettings: `repeat(${menuValue.formFactor.columns}, 1fr)`,
      rowsSettings: `repeat(${menuValue.formFactor.rows}, 1fr)`,
      layoutType: menuValue.formFactor.type
    };

    if (menuValue.formFactor.type === LayoutType.CUSTOM) {
      layoutSettings.cardsAreaSettings = [];
      switch (menuValue.cardsAmount) {
        case 3:
          if (menuValue.name === LayoutNameEnum.LAYOUT_THREE_CARD_VERTICAL_CUSTOM_ONE) {
            layoutSettings.cardsAreaSettings.push({row: '1 / 3', cardIndex: 0});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_THREE_CARD_HORIZONTAL_CUSTOM_ONE) {
            layoutSettings.cardsAreaSettings.push({column: '1 / 3', cardIndex: 2});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_THREE_CARD_HORIZONTAL_CUSTOM_TWO) {
            layoutSettings.cardsAreaSettings.push({column: '1 / 3', cardIndex: 0});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_THREE_CARD_VERTICAL_CUSTOM_TWO) {
            layoutSettings.cardsAreaSettings.push({row: '1 / 1', cardIndex: 0});
            layoutSettings.cardsAreaSettings.push({row: '1 / 3', cardIndex: 1});
          }
          break;

        case 4:
          if (menuValue.name === LayoutNameEnum.LAYOUT_FOUR_CARD_VERTICAL_CUSTOM_ONE) {
            layoutSettings.cardsAreaSettings.push({row: '1 / 4', cardIndex: 0});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_FOUR_CARD_HORIZONTAL_CUSTOM_ONE) {
            layoutSettings.cardsAreaSettings.push({column: '1 / 4', cardIndex: 0});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_FOUR_CARD_HORIZONTAL_CUSTOM_TWO) {
            layoutSettings.cardsAreaSettings.push({column: '1 / 3', cardIndex: 2});
            layoutSettings.cardsAreaSettings.push({column: '1 / 3', cardIndex: 3});
          }
          break;
        case 5:
          if (menuValue.name === LayoutNameEnum.LAYOUT_FIVE_CARD_HORIZONTAL) {
            layoutSettings.cardsAreaSettings.push({column: '1 / 5', cardIndex: 0});
          }

          if (menuValue.name === LayoutNameEnum.LAYOUT_FIVE_CARD_VERTICAL) {
            layoutSettings.cardsAreaSettings.push({row: '1 / 5', cardIndex: 0});
          }
          break;
      }
    }

    return layoutSettings;
  }

  public getParametersChart(chartType: ChartType, allParameters: IChartFilter[]): IChartFilter[] {
    const resultParameters: IChartFilter[] = allParameters
      .filter((parameter: IChartFilter) => {
        if (parameter.parameterId === ChartFilterPatternNameEnum.STOCK_ID && chartType !== ChartTypeEnum.IV_DIFFERENCE) {
          return parameter;
        }

        switch (chartType) {
          case ChartTypeEnum.SPOT_CHART:
            return parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE;
          case ChartTypeEnum.IVX_HISTORY:
            return (parameter.parameterId === ChartFilterPatternNameEnum.IVX_TERM && parameter.name === 'IVX Term') ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME;
          case ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME:
            return parameter.parameterId === ChartFilterPatternNameEnum.OPTION_TYPE ||
              parameter.parameterId === CustomControlsNameEnum.TOTAL_OI_CHART_TYPE ||
              parameter.parameterId === ChartFilterPatternNameEnum.EOD_TIME_RANGE;
          case ChartTypeEnum.HISTORICAL_VOLATILITY:
            return parameter.parameterId === ChartFilterPatternNameEnum.HV_TERM ||
              parameter.parameterId === ChartFilterPatternNameEnum.EOD_TIME_RANGE;
          case ChartTypeEnum.IMPLIED_VOLATILITY:
            return parameter.parameterId === ChartFilterPatternNameEnum.MONEYNESS ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
              (parameter.parameterId === ChartFilterPatternNameEnum.IVX_TERM && parameter.name === 'Period');
          case ChartTypeEnum.PREMIUM_CHART:
            return (parameter.parameterId === ChartFilterPatternNameEnum.IVX_TERM && parameter.name === 'IVX Term') ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
              parameter.parameterId === ChartFilterPatternNameEnum.HV_TERM;
          case ChartTypeEnum.TERM_STRUCTURE_ROLLING:
            return parameter.parameterId === ChartFilterPatternNameEnum.MONEYNESS ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.DATE_TIME;
          case ChartTypeEnum.SKEW_CHART:
            return (parameter.parameterId === ChartFilterPatternNameEnum.IVX_TERM && parameter.name === 'Period') ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.DATE_TIME;
          case ChartTypeEnum.IV_DIFFERENCE:
            return (parameter.parameterId === ChartFilterPatternNameEnum.IVX_TERM && parameter.name === 'Period') ||
              parameter.parameterId === ChartFilterPatternNameEnum.STOCK_ID ||
              parameter.parameterId === ChartFilterPatternNameEnum.MONEYNESS ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
              parameter.parameterId === ChartFilterPatternNameEnum.REALTIME;
          case ChartTypeEnum.SINGLE_CONTRACT_ANALYZER:
            return parameter.parameterId === ChartFilterPatternNameEnum.REALTIME ||
              parameter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
              parameter.parameterId === CustomControlsNameEnum.EXPIRATION_DATE ||
              parameter.parameterId === CustomControlsNameEnum.STRIKE ||
              parameter.parameterId === ChartFilterPatternNameEnum.OPTION_TYPE ||
              parameter.parameterId === CustomControlsNameEnum.SINGLE_CONTRACT_ANALYZER_TYPE;
        }
      });

    if (chartType === ChartTypeEnum.IV_DIFFERENCE) {
      return resultParameters.map((filter: IChartFilter) => {
        if (
          filter.parameterId === ChartFilterPatternNameEnum.IVX_TERM ||
          filter.parameterId === ChartFilterPatternNameEnum.STOCK_ID ||
          filter.parameterId === ChartFilterPatternNameEnum.MONEYNESS
        ) {
          return [{...filter, parameterId: `${filter.parameterId}-first`}, {...filter, parameterId: `${filter.parameterId}-second`}];
        }
        return filter;
      }).flat() as IChartFilter[];
    }

    if (chartType === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
      return resultParameters.map((filter: IChartFilter) => {
        if (filter.parameterId === ChartFilterPatternNameEnum.OPTION_TYPE) {
          return {...filter, pattern: filter.pattern.filter((param: IChartFilterPattern<OptionType>) => param.name !== OptionTypeEnum.CALL_PUT)};
        }

        return filter;
      });
    }

    return resultParameters;
  }
}
